import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Checkbox,
  Collapse,
  IconButton,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Divider,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, RepeatIcon } from '@chakra-ui/icons';
import { apiConfig } from '../config/api';

interface MonitoringData {
  voice_health?: {
    connection_status?: string;
    active_sessions?: number;
    gateway_latency?: number;
    queue_depth?: number;
  };
  system_metrics?: {
    cpu_percent?: number;
    memory_percent?: number;
    disk_percent?: number;
    thread_count?: number;
    pid?: string;
  };
  internal_services?: Array<{
    name: string;
    status: string;
    port?: number;
  }>;
  external_providers?: Array<{
    name: string;
    status: string;
  }>;
  recent_logs?: any[];
  extras?: any;
}

export const AdminMonitoring: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const toast = useToast();

  const fetchMonitoringData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Added 2026-02-26: Include Bearer token for backend HTTPBearer() auth (mirrors AdminDashboard.tsx pattern)
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(`${apiConfig.baseUrl}/admin/monitoring`, {
        credentials: 'include', // Send session cookie
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required');
        } else if (response.status === 401) {
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const monitoringData = await response.json();
      setData(monitoringData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch monitoring data';
      setError(errorMessage);
      console.error('Monitoring fetch error:', err);
      
      toast({
        title: 'Monitoring Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh when enabled
  useEffect(() => {
    if (isExpanded && autoRefresh) {
      fetchMonitoringData();
      const interval = setInterval(fetchMonitoringData, 5000); // Every 5 seconds
      return () => clearInterval(interval);
    }
  }, [isExpanded, autoRefresh]);

  // Fetch on first expand
  useEffect(() => {
    if (isExpanded && !data) {
      fetchMonitoringData();
    }
  }, [isExpanded]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.700"
      bg="gray.800"
      mb={4}
    >
      {/* Header - Always Visible */}
      <Button
        onClick={toggleExpand}
        w="full"
        justifyContent="space-between"
        variant="ghost"
        colorScheme="purple"
        size="lg"
        _hover={{ bg: 'gray.750' }}
        rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        <HStack spacing={2}>
          <Heading size="md" color="purple.400">
            System Monitoring
          </Heading>
          {loading && <Spinner size="sm" color="gray.400" />}
        </HStack>
      </Button>

      {/* Expandable Content */}
      <Collapse in={isExpanded} animateOpacity>
        <Box p={4} borderTopWidth="1px" borderColor="gray.700">
          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {data && (
            <VStack spacing={4} align="stretch">
              {/* Voice & Bot Health */}
              {data.voice_health && (
                <Box>
                  <Heading size="sm" color="cyan.400" mb={2}>
                    Voice & Bot Health
                  </Heading>
                  <Box bg="gray.900" p={3} borderRadius="md">
                    <VStack spacing={1} align="stretch" fontSize="sm">
                      <Flex justify="space-between">
                        <Text>Connection:</Text>
                        <Text color={data.voice_health.connection_status === 'connected' ? 'green.400' : 'red.400'}>
                          {data.voice_health.connection_status || 'unknown'}
                        </Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Active Sessions:</Text>
                        <Text>{data.voice_health.active_sessions ?? 0}</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Gateway Latency:</Text>
                        <Text>{data.voice_health.gateway_latency ?? 0}ms</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Queue Depth:</Text>
                        <Text>{data.voice_health.queue_depth ?? 0}</Text>
                      </Flex>
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* System Metrics */}
              {data.system_metrics && (
                <Box>
                  <Heading size="sm" color="cyan.400" mb={2}>
                    System Metrics
                  </Heading>
                  <Box bg="gray.900" p={3} borderRadius="md">
                    <VStack spacing={1} align="stretch" fontSize="sm">
                      <Flex justify="space-between">
                        <Text>CPU:</Text>
                        <Text>{data.system_metrics.cpu_percent?.toFixed(1) ?? 0}%</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Memory:</Text>
                        <Text>{data.system_metrics.memory_percent?.toFixed(1) ?? 0}%</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Disk:</Text>
                        <Text>{data.system_metrics.disk_percent?.toFixed(1) ?? 0}%</Text>
                      </Flex>
                      <Flex justify="space-between">
                        <Text>Threads:</Text>
                        <Text>{data.system_metrics.thread_count ?? 0}</Text>
                      </Flex>
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Internal Services */}
              {data.internal_services && data.internal_services.length > 0 && (
                <Box>
                  <Heading size="sm" color="cyan.400" mb={2}>
                    Internal Services
                  </Heading>
                  <Box bg="gray.900" p={3} borderRadius="md">
                    <VStack spacing={1} align="stretch" fontSize="sm">
                      {data.internal_services.map((service, idx) => (
                        <Flex key={idx} justify="space-between">
                          <Text>
                            {service.name}
                            {service.port && <Text as="span" color="gray.500"> :{service.port}</Text>}
                          </Text>
                          <Text color={service.status === 'online' ? 'green.400' : 'red.400'}>
                            {service.status}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* External Providers */}
              {data.external_providers && data.external_providers.length > 0 && (
                <Box>
                  <Heading size="sm" color="cyan.400" mb={2}>
                    External Providers
                  </Heading>
                  <Box bg="gray.900" p={3} borderRadius="md">
                    <VStack spacing={1} align="stretch" fontSize="sm">
                      {data.external_providers.map((provider, idx) => (
                        <Flex key={idx} justify="space-between">
                          <Text>{provider.name}</Text>
                          <Text color={provider.status === 'configured' ? 'green.400' : 'yellow.400'}>
                            {provider.status}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Recent Logs (last 20) */}
              {data.recent_logs && data.recent_logs.length > 0 && (
                <Box>
                  <Heading size="sm" color="cyan.400" mb={2}>
                    Recent Logs (last 20)
                  </Heading>
                  <Box bg="gray.900" p={3} borderRadius="md" maxH="64" overflowY="auto">
                    <Code
                      display="block"
                      whiteSpace="pre-wrap"
                      fontSize="xs"
                      color="gray.300"
                      bg="transparent"
                    >
                      {JSON.stringify(data.recent_logs, null, 2)}
                    </Code>
                  </Box>
                </Box>
              )}

              {/* Controls */}
              <Divider borderColor="gray.700" />
              <Flex gap={2} align="center">
                <Button
                  onClick={fetchMonitoringData}
                  isLoading={loading}
                  loadingText="Refreshing..."
                  colorScheme="purple"
                  size="sm"
                  leftIcon={<RepeatIcon />}
                >
                  Refresh Now
                </Button>
                <Checkbox
                  isChecked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  colorScheme="purple"
                  size="sm"
                >
                  Auto-refresh (5s)
                </Checkbox>
              </Flex>
            </VStack>
          )}

          {!data && !loading && !error && (
            <Text color="gray.500" textAlign="center">
              Click "Refresh Now" to load monitoring data
            </Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
